/**
 * Homestay and Villa Owner Feed Landing Page (/owner/<token>).
 * R39 K9.b (SO-P121, SO-P122; SO-D202, SO-D300, SO-D207, SO-P68).
 */

import React, { useEffect, useState, useCallback } from 'react'
import { useNls } from '../../nls/nlsProvider'
import {
  getOwnerFeedLinkInfo,
  prepareOwnerFeedOtp,
  openOwnerFeed,
} from '../../services/opsOwnerFeedService'
import { sendOutsidePhoneOtp } from '../../services/outsidePhoneOtp'
import { getApprovalsAuth, signOutApprovals } from '../../services/approvalsApp'
import type { ConfirmationResult } from 'firebase/auth'
import type { OpsOwnerDigestSnapshot } from '../../types/ops'
import { Button } from '../Common/Button'
import './ownerFeedLanding.css'

export interface OwnerFeedLandingProps {
  token: string
}

type Step = 'LOADING' | 'INVALID' | 'LINK_INFO' | 'OTP' | 'FEED'

export function OwnerFeedLanding({ token }: OwnerFeedLandingProps): React.ReactElement {
  const { t, locale } = useNls()

  const [step, setStep] = useState<Step>('LOADING')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<boolean>(false)

  // Step 1: Link info
  const [linkInfo, setLinkInfo] = useState<{
    propertyName: string
    ownerName: string
    phoneMasked: string
  } | null>(null)

  // Step 2: Phone input & OTP
  const [phone, setPhone] = useState<string>('')
  const [otpCode, setOtpCode] = useState<string>('')
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null)

  // Step 3: Feed view
  const [feedPropertyName, setFeedPropertyName] = useState<string>('')
  const [feedOwnerName, setFeedOwnerName] = useState<string>('')
  const [digests, setDigests] = useState<
    Array<{
      dateKey: string
      builtAt: number
      snapshot: OpsOwnerDigestSnapshot
    }>
  >([])
  const [selectedDateKey, setSelectedDateKey] = useState<string>('')
  const [failedPhotoUrls, setFailedPhotoUrls] = useState<Set<string>>(() => new Set())
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState<string | null>(null)

  const formatTimestamp = useCallback(
    (timestamp: number) => {
      try {
        return new Intl.DateTimeFormat(locale, {
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(new Date(timestamp))
      } catch {
        return new Date(timestamp).toLocaleString()
      }
    },
    [locale],
  )

  // 1. Initial link info fetch
  useEffect(() => {
    let cancelled = false

    async function loadLink() {
      if (!token) {
        setStep('INVALID')
        return
      }

      try {
        const info = await getOwnerFeedLinkInfo({ token })
        if (cancelled) return

        if (!info.propertyName || !info.phoneMasked) {
          setStep('INVALID')
          return
        }

        setLinkInfo(info)
        setStep('LINK_INFO')
      } catch {
        if (cancelled) return
        setStep('INVALID')
      }
    }

    void loadLink()

    return () => {
      cancelled = true
    }
  }, [token])

  // 2. Handle Send OTP with local last-4 check
  const handleSendOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!linkInfo) return
    setError(null)

    const expectedLast4 = linkInfo.phoneMasked.replace(/\D/g, '').slice(-4)
    const enteredDigits = phone.replace(/\D/g, '')
    const enteredLast4 = enteredDigits.slice(-4)

    // Local last-4 check blocks before calling prepareOwnerFeedOtp
    if (enteredLast4 !== expectedLast4 || enteredDigits.length < 4) {
      setError(t('NLSOpsOwnerFeedPhoneMismatch'))
      return
    }

    setBusy(true)
    try {
      const prep = await prepareOwnerFeedOtp({ token, last4: enteredLast4 })
      if (!prep.success || prep.attemptsRemaining <= 0) {
        setError(t('NLSOpsOwnerFeedCapExhausted'))
        setBusy(false)
        return
      }

      const formattedPhone = phone.trim().startsWith('+') ? phone.trim() : `+${phone.trim()}`
      const confirmation = await sendOutsidePhoneOtp(getApprovalsAuth(), formattedPhone)
      setConfirmationResult(confirmation)
      setStep('OTP')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      if (
        msg.includes('resource-exhausted') ||
        msg.includes('too-many-requests') ||
        msg.includes('attempts')
      ) {
        setError(t('NLSOpsOwnerFeedCapExhausted'))
      } else {
        setError(t('NLSOpsOwnerFeedGenericError'))
      }
    } finally {
      setBusy(false)
    }
  }

  // 3. Handle Verify OTP
  const handleVerifyOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!confirmationResult) return
    setError(null)

    if (!otpCode || otpCode.trim().length !== 6) {
      setError(t('NLSOpsOwnerFeedInvalidOtp'))
      return
    }

    setBusy(true)
    try {
      await confirmationResult.confirm(otpCode.trim())
      const openRes = await openOwnerFeed({ token })
      setFeedPropertyName(openRes.propertyName || linkInfo?.propertyName || '')
      setFeedOwnerName(openRes.ownerName || linkInfo?.ownerName || '')
      setDigests(openRes.digests || [])
      if (openRes.digests && openRes.digests.length > 0) {
        setSelectedDateKey(openRes.digests[0].dateKey)
      }
      setStep('FEED')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      if (msg.includes('invalid-verification-code') || msg.includes('code')) {
        setError(t('NLSOpsOwnerFeedInvalidOtp'))
      } else if (msg.includes('permission-denied') || msg.includes('does not match')) {
        setError(t('NLSOpsOwnerFeedPhoneMismatch'))
      } else {
        setError(t('NLSOpsOwnerFeedGenericError'))
      }
    } finally {
      setBusy(false)
    }
  }

  // 4. Handle Refresh (re-runs openOwnerFeed without new OTP)
  const handleRefresh = async () => {
    setError(null)
    setBusy(true)
    try {
      const openRes = await openOwnerFeed({ token })
      setFeedPropertyName(openRes.propertyName || feedPropertyName)
      setFeedOwnerName(openRes.ownerName || feedOwnerName)
      setDigests(openRes.digests || [])
      if (openRes.digests && openRes.digests.length > 0) {
        if (!openRes.digests.some((d) => d.dateKey === selectedDateKey)) {
          setSelectedDateKey(openRes.digests[0].dateKey)
        }
      }
      setFailedPhotoUrls(new Set())
    } catch {
      setError(t('NLSOpsOwnerFeedGenericError'))
    } finally {
      setBusy(false)
    }
  }

  // 5. Handle Sign out
  const handleSignOut = async () => {
    setBusy(true)
    try {
      await signOutApprovals()
    } catch {
      // ignore cleanup errors
    } finally {
      setConfirmationResult(null)
      setOtpCode('')
      setDigests([])
      setSelectedDateKey('')
      setBusy(false)
      setStep('LINK_INFO')
    }
  }

  const markPhotoFailed = (url: string) => {
    setFailedPhotoUrls((prev) => {
      const next = new Set(prev)
      next.add(url)
      return next
    })
  }

  const activeDigest = digests.find((d) => d.dateKey === selectedDateKey) || digests[0]
  const snapshot = activeDigest?.snapshot

  return (
    <main className="ownerFeed__container" aria-label={t('NLSOpsOwnerFeedVerifyTitle')}>
      <div className="ownerFeed__card">
        {step === 'LOADING' && (
          <div className="ownerFeed__header">
            <h1 className="ownerFeed__title">{t('NLSOpsOwnerFeedLoading')}</h1>
          </div>
        )}

        {step === 'INVALID' && (
          <div className="ownerFeed__invalid">
            <h1 className="ownerFeed__title">{t('NLSOpsOwnerFeedInvalidTitle')}</h1>
            <p className="ownerFeed__messageBody">{t('NLSOpsOwnerFeedInvalidBody')}</p>
          </div>
        )}

        {step === 'LINK_INFO' && linkInfo && (
          <>
            <div className="ownerFeed__header">
              <h1 className="ownerFeed__title">{t('NLSOpsOwnerFeedVerifyTitle')}</h1>
              <p className="ownerFeed__subtitle">{t('NLSOpsOwnerFeedVerifySubtitle')}</p>
            </div>

            {error && (
              <div className="ownerFeed__errorBanner" role="alert">
                {error}
              </div>
            )}

            <div className="ownerFeed__infoGrid">
              <div className="ownerFeed__infoRow">
                <span className="ownerFeed__infoLabel">{t('NLSOpsOwnerFeedPropertyLabel')}</span>
                <span className="ownerFeed__infoValue">{linkInfo.propertyName}</span>
              </div>
              <div className="ownerFeed__infoRow">
                <span className="ownerFeed__infoLabel">{t('NLSOpsOwnerFeedOwnerLabel')}</span>
                <span className="ownerFeed__infoValue">{linkInfo.ownerName}</span>
              </div>
              <div className="ownerFeed__infoRow">
                <span className="ownerFeed__infoLabel">{t('NLSOpsOwnerFeedPhoneLabel')}</span>
                <span className="ownerFeed__infoValue">{linkInfo.phoneMasked}</span>
              </div>
            </div>

            <form onSubmit={handleSendOtp} className="ownerFeed__form">
              <div className="ownerFeed__field">
                <label htmlFor="owner-phone" className="ownerFeed__label">
                  {t('NLSOpsOwnerFeedPhoneLabel')}
                </label>
                <input
                  id="owner-phone"
                  type="tel"
                  className="ownerFeed__input"
                  placeholder={t('NLSOpsOwnerFeedPhonePlaceholder')}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={busy}
                  required
                />
              </div>

              <div className="ownerFeed__actions">
                <Button
                  variant="primary"
                  size="lg"
                  type="submit"
                  disabled={busy || phone.replace(/\D/g, '').length < 4}
                >
                  {busy ? t('NLSOpsOwnerFeedLoading') : t('NLSOpsOwnerFeedSendOtpBtn')}
                </Button>
              </div>
            </form>
          </>
        )}

        {step === 'OTP' && (
          <>
            <div className="ownerFeed__header">
              <h1 className="ownerFeed__title">{t('NLSOpsOwnerFeedOtpTitle')}</h1>
              <p className="ownerFeed__subtitle">{t('NLSOpsOwnerFeedOtpSubtitle')}</p>
            </div>

            {error && (
              <div className="ownerFeed__errorBanner" role="alert">
                {error}
              </div>
            )}

            <form onSubmit={handleVerifyOtp} className="ownerFeed__form">
              <div className="ownerFeed__field">
                <label htmlFor="owner-otp" className="ownerFeed__label">
                  {t('NLSOpsOwnerFeedOtpLabel')}
                </label>
                <input
                  id="owner-otp"
                  type="text"
                  className="ownerFeed__input"
                  placeholder="123456"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  disabled={busy}
                  required
                />
              </div>

              <div className="ownerFeed__actions">
                <Button
                  variant="primary"
                  size="lg"
                  type="submit"
                  disabled={busy || otpCode.trim().length !== 6}
                >
                  {busy ? t('NLSOpsOwnerFeedLoading') : t('NLSOpsOwnerFeedVerifyOtpBtn')}
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  type="button"
                  onClick={() => {
                    setError(null)
                    setStep('LINK_INFO')
                  }}
                  disabled={busy}
                >
                  {t('NLSOpsOwnerFeedChangePhoneBtn')}
                </Button>
              </div>
            </form>
          </>
        )}

        {step === 'FEED' && (
          <div className="ownerFeed__feedShell">
            {/* Top Bar with Property Name, Owner Name, Refresh, Sign Out */}
            <div className="ownerFeed__feedTopBar">
              <div>
                <h1 className="ownerFeed__title">{feedPropertyName}</h1>
                <p className="ownerFeed__subtitle">{feedOwnerName}</p>
              </div>
              <div className="ownerFeed__topBarActions">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={busy}
                >
                  {t('NLSOpsOwnerFeedRefreshBtn')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleSignOut}
                  disabled={busy}
                >
                  {t('NLSOpsOwnerFeedSignOutBtn')}
                </Button>
              </div>
            </div>

            {error && (
              <div className="ownerFeed__errorBanner" role="alert">
                {error}
              </div>
            )}

            {digests.length === 0 ? (
              <p className="ownerFeed__emptyText">{t('NLSOpsOwnerFeedNoDigests')}</p>
            ) : (
              <>
                {/* Date Tabs */}
                <div className="ownerFeed__dateTabs" role="tablist" aria-label={t('NLSOpsOwnerFeedToday')}>
                  {digests.map((d, idx) => {
                    const isSelected = d.dateKey === selectedDateKey
                    return (
                      <button
                        key={d.dateKey}
                        type="button"
                        role="tab"
                        aria-selected={isSelected}
                        className={`ownerFeed__dateTab ${isSelected ? 'ownerFeed__dateTab--active' : ''}`}
                        onClick={() => setSelectedDateKey(d.dateKey)}
                      >
                        {idx === 0 ? t('NLSOpsOwnerFeedToday') : d.dateKey}
                      </button>
                    )
                  })}
                </div>

                {snapshot && (
                  <>
                    {/* Summary Line */}
                    <div className="ownerFeed__summaryBanner">
                      {t('NLSOpsOwnerFeedSummaryLine', {
                        ready: String(snapshot.rooms?.totalReadyToday ?? snapshot.rooms?.readyToday?.length ?? 0),
                        onDuty: String(snapshot.attendance?.shiftsPresentCount ?? 0),
                        issues: String(snapshot.issues?.totalOpen ?? snapshot.issues?.openIssues?.length ?? 0),
                      })}
                    </div>

                    {/* Rooms Card */}
                    <section className="ownerFeed__sectionCard" aria-label={t('NLSOpsOwnerFeedRoomsTitle')}>
                      <div className="ownerFeed__sectionHeader">
                        <h2 className="ownerFeed__sectionTitle">{t('NLSOpsOwnerFeedRoomsTitle')}</h2>
                      </div>

                      {/* Room State Chips */}
                      <div className="ownerFeed__chipGroup">
                        {Object.entries(snapshot.rooms?.countsByHk || {}).map(([state, count]) => (
                          <span key={`hk-${state}`} className="ownerFeed__chip">
                            {state}: {count}
                          </span>
                        ))}
                        {Object.entries(snapshot.rooms?.countsByOccupancy || {}).map(([state, count]) => (
                          <span key={`occ-${state}`} className="ownerFeed__chip">
                            {state}: {count}
                          </span>
                        ))}
                        {Object.entries(snapshot.rooms?.countsByService || {}).map(([state, count]) => (
                          <span key={`srv-${state}`} className="ownerFeed__chip">
                            {state}: {count}
                          </span>
                        ))}
                      </div>

                      {/* Rooms ready today */}
                      <div>
                        <p className="ownerFeed__infoLabel">{t('NLSOpsOwnerFeedReadyRoomsSubtitle')}</p>
                        {(!snapshot.rooms?.readyToday || snapshot.rooms.readyToday.length === 0) ? (
                          <p className="ownerFeed__emptyText">{t('NLSOpsOwnerFeedNoReadyRooms')}</p>
                        ) : (
                          <div className="ownerFeed__roomList">
                            {snapshot.rooms.readyToday.map((room) => (
                              <div key={room.roomId} className="ownerFeed__roomItem">
                                <div className="ownerFeed__roomHeader">
                                  <span className="ownerFeed__roomLabel">{room.roomLabel}</span>
                                </div>
                                <div className="ownerFeed__roomPhotos">
                                  {room.photos.map((photoUrl, pIdx) => {
                                    const hasFailed = failedPhotoUrls.has(photoUrl)
                                    if (hasFailed) {
                                      return (
                                        <div
                                          key={`fail-${pIdx}`}
                                          className="ownerFeed__photoPlaceholder"
                                        >
                                          {t('NLSOpsOwnerFeedPhotoPlaceholder')}
                                        </div>
                                      )
                                    }
                                    return (
                                      <img
                                        key={photoUrl}
                                        src={photoUrl}
                                        alt={`${room.roomLabel} photo ${pIdx + 1}`}
                                        className="ownerFeed__photoThumbnail"
                                        onError={() => markPhotoFailed(photoUrl)}
                                        onClick={() => setPreviewPhotoUrl(photoUrl)}
                                      />
                                    )
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </section>

                    {/* Attendance Card */}
                    <section className="ownerFeed__sectionCard" aria-label={t('NLSOpsOwnerFeedAttendanceTitle')}>
                      <div className="ownerFeed__sectionHeader">
                        <h2 className="ownerFeed__sectionTitle">{t('NLSOpsOwnerFeedAttendanceTitle')}</h2>
                        <span className="ownerFeed__chip">
                          {t('NLSOpsOwnerFeedOnDutyCount', {
                            count: String(snapshot.attendance?.shiftsPresentCount ?? 0),
                          })}
                        </span>
                      </div>

                      <div className="ownerFeed__chipGroup">
                        {Object.entries(snapshot.attendance?.countsByStatus || {}).map(([st, cnt]) => (
                          <span key={`att-${st}`} className="ownerFeed__chip">
                            {st}: {cnt}
                          </span>
                        ))}
                      </div>

                      {(!snapshot.attendance?.persons || snapshot.attendance.persons.length === 0) ? (
                        <p className="ownerFeed__emptyText">{t('NLSOpsOwnerFeedNoAttendance')}</p>
                      ) : (
                        <div className="ownerFeed__personList">
                          {snapshot.attendance.persons.map((person, idx) => (
                            <div key={`person-${idx}`} className="ownerFeed__personRow">
                              <div className="ownerFeed__personInfo">
                                <span className="ownerFeed__personName">{person.displayName}</span>
                                {(person.actualIn || person.actualOut) && (
                                  <span className="ownerFeed__personTimes">
                                    {person.actualIn && t('NLSOpsOwnerFeedInTime', { time: person.actualIn })}
                                    {person.actualIn && person.actualOut && ' · '}
                                    {person.actualOut && t('NLSOpsOwnerFeedOutTime', { time: person.actualOut })}
                                  </span>
                                )}
                              </div>
                              <span className="ownerFeed__chip">{person.status}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>

                    {/* Stock Card (only when stock is present in snapshot) */}
                    {snapshot.stock && (
                      <section className="ownerFeed__sectionCard" aria-label={t('NLSOpsOwnerFeedStockTitle')}>
                        <div className="ownerFeed__sectionHeader">
                          <h2 className="ownerFeed__sectionTitle">{t('NLSOpsOwnerFeedStockTitle')}</h2>
                          <span className="ownerFeed__sectionNotice">{t('NLSOpsOwnerFeedLowStockSubtitle')}</span>
                        </div>

                        {(!snapshot.stock.lowStockItems || snapshot.stock.lowStockItems.length === 0) ? (
                          <p className="ownerFeed__emptyText">{t('NLSOpsOwnerFeedNoLowStock')}</p>
                        ) : (
                          <div className="ownerFeed__itemList">
                            {snapshot.stock.lowStockItems.map((item) => (
                              <div key={item.id} className="ownerFeed__itemRow">
                                <div className="ownerFeed__itemPrimary">
                                  <span className="ownerFeed__itemName">{item.name}</span>
                                  <span className="ownerFeed__itemMeta">
                                    {item.locationName || item.locationId}
                                  </span>
                                </div>
                                <span className="ownerFeed__chip">
                                  {item.qty} {item.unit}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </section>
                    )}

                    {/* Issues Card */}
                    <section className="ownerFeed__sectionCard" aria-label={t('NLSOpsOwnerFeedIssuesTitle')}>
                      <div className="ownerFeed__sectionHeader">
                        <h2 className="ownerFeed__sectionTitle">{t('NLSOpsOwnerFeedIssuesTitle')}</h2>
                      </div>

                      {(!snapshot.issues?.openIssues || snapshot.issues.openIssues.length === 0) ? (
                        <p className="ownerFeed__emptyText">{t('NLSOpsOwnerFeedNoIssues')}</p>
                      ) : (
                        <>
                          <div className="ownerFeed__itemList">
                            {snapshot.issues.openIssues.map((issue) => (
                              <div key={issue.id} className="ownerFeed__itemRow">
                                <div className="ownerFeed__itemPrimary">
                                  <span className="ownerFeed__itemName">{issue.title}</span>
                                  <span className="ownerFeed__itemMeta">
                                    {issue.kindLabel} · {issue.placeLabel} · {t('NLSOpsOwnerFeedDaysAgo', { days: String(issue.ageDays) })}
                                  </span>
                                </div>
                                <span className="ownerFeed__chip">{issue.state}</span>
                              </div>
                            ))}
                          </div>
                          {(snapshot.issues.totalOpen > snapshot.issues.openIssues.length) && (
                            <p className="ownerFeed__trimmedNote">
                              {t('NLSOpsOwnerFeedShowingTrimmed', {
                                shown: String(snapshot.issues.openIssues.length),
                                total: String(snapshot.issues.totalOpen),
                              })}
                            </p>
                          )}
                        </>
                      )}
                    </section>

                    {/* Approvals Card */}
                    <section className="ownerFeed__sectionCard" aria-label={t('NLSOpsOwnerFeedApprovalsTitle')}>
                      <div className="ownerFeed__sectionHeader">
                        <h2 className="ownerFeed__sectionTitle">{t('NLSOpsOwnerFeedApprovalsTitle')}</h2>
                      </div>

                      <p className="ownerFeed__sectionNotice">{t('NLSOpsOwnerFeedApprovalsNotice')}</p>

                      {(!snapshot.approvals?.pendingApprovals || snapshot.approvals.pendingApprovals.length === 0) ? (
                        <p className="ownerFeed__emptyText">{t('NLSOpsOwnerFeedNoApprovals')}</p>
                      ) : (
                        <div className="ownerFeed__itemList">
                          {snapshot.approvals.pendingApprovals.map((appr) => (
                            <div key={appr.id} className="ownerFeed__itemRow">
                              <div className="ownerFeed__itemPrimary">
                                <span className="ownerFeed__itemName">{appr.title}</span>
                                <span className="ownerFeed__itemMeta">
                                  {t('NLSOpsOwnerFeedSentAt', { time: formatTimestamp(appr.sentAt) })}
                                  {' · '}
                                  {t('NLSOpsOwnerFeedExpiresAt', { time: formatTimestamp(appr.expiresAt) })}
                                </span>
                              </div>
                              <span className="ownerFeed__chip">{appr.status}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Full Image Preview Modal */}
        {previewPhotoUrl && (
          <div className="ownerFeed__previewModal" onClick={() => setPreviewPhotoUrl(null)}>
            <img
              src={previewPhotoUrl}
              alt="Room preview"
              className="ownerFeed__previewImg"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="ownerFeed__previewActions">
              <Button variant="secondary" size="md" onClick={() => setPreviewPhotoUrl(null)}>
                {t('NLSOpsOwnerFeedClosePreview')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </main>
  )
}

export default OwnerFeedLanding
